import {supabase, testConnection, initializeSupabase} from '../lib/supabase';

let isSupabaseConnected = false;

// Initialize database connection
export const initializeDatabase = async () => {
  try {
    isSupabaseConnected = await initializeSupabase();
    return isSupabaseConnected;
  } catch (error) {
    console.error('Database initialization error:', error);
    return false;
  }
};

// Check localStorage quota
const checkStorageQuota = () => {
  try {
    let totalSize = 0;
    // Fixed: Use Object.prototype.hasOwnProperty.call instead of direct access
    for (let key in localStorage) {
      if (Object.prototype.hasOwnProperty.call(localStorage, key)) {
        totalSize += localStorage[key].length + key.length;
      }
    }
    
    const sizeInMB = (totalSize / (1024 * 1024)).toFixed(2);
    const isNearLimit = totalSize > 4 * 1024 * 1024; // 4MB warning threshold
    
    console.log(`📊 localStorage usage: ${sizeInMB}MB`);
    
    return {
      totalSize,
      sizeInMB,
      isNearLimit,
      remaining: Math.max(0, 5 * 1024 * 1024 - totalSize) // Assume 5MB limit
    };
  } catch (error) {
    console.error('Error checking storage quota:', error);
    return { totalSize: 0, sizeInMB: 0, isNearLimit: false, remaining: 0 };
  }
};

// Clean up localStorage to free space
const cleanupLocalStorage = () => {
  try {
    console.log('🧹 Cleaning up localStorage to free space...');
    
    // Remove old/large items first
    const keysToClean = ['temp_images', 'cache_', 'old_'];
    
    for (let key in localStorage) {
      // Fixed: Use Object.prototype.hasOwnProperty.call instead of direct access
      if (Object.prototype.hasOwnProperty.call(localStorage, key)) {
        // Remove temporary or cache items
        if (keysToClean.some(prefix => key.startsWith(prefix))) {
          localStorage.removeItem(key);
          console.log(`🗑️ Removed: ${key}`);
        }
        
        // Remove very large single items (over 1MB)
        if (localStorage[key] && localStorage[key].length > 1024 * 1024) {
          console.log(`⚠️ Large item detected: ${key} (${(localStorage[key].length / 1024).toFixed(0)}KB)`);
          // Don't auto-remove, just log for now
        }
      }
    }
    
    return checkStorageQuota();
  } catch (error) {
    console.error('Error cleaning localStorage:', error);
    return checkStorageQuota();
  }
};

// Optimize data before saving to localStorage
const optimizeForLocalStorage = (data) => {
  try {
    let optimized = { ...data };
    
    // Handle large images
    if (optimized.image && optimized.image.startsWith('data:image/')) {
      const sizeKB = optimized.image.length / 1024;
      if (sizeKB > 500) { // If image is over 500KB
        console.log(`🖼️ Large image detected (${sizeKB.toFixed(0)}KB), using placeholder`);
        // Use a placeholder or compress
        optimized.image = 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&h=400&fit=crop';
        optimized._hasLargeImage = true; // Flag for UI
      }
    }
    
    // Truncate very long content for localStorage
    if (optimized.content && optimized.content.length > 50000) {
      console.log('📝 Truncating long content for localStorage');
      optimized.content = optimized.content.substring(0, 50000) + '... [Content truncated in localStorage fallback]';
      optimized._truncated = true;
    }
    
    return optimized;
  } catch (error) {
    console.error('Error optimizing data:', error);
    return data;
  }
};

// Generic database operations
export const dbOperation = {
  // Blog Posts
  async saveBlogPost(post) {
    console.log('🔄 Saving blog post:', post.id, post.title);
    
    if (!isSupabaseConnected) {
      console.log('📝 Using localStorage fallback for blog post');
      return this.saveToLocalStorage('blog_posts', post);
    }

    try {
      // Prepare the data for Supabase (match column names)
      const supabaseData = {
        id: post.id,
        title: post.title,
        excerpt: post.excerpt,
        content: post.content,
        category: post.category,
        author: post.author,
        date: post.date,
        read_time: post.readTime || post.read_time,
        status: post.status,
        featured: post.featured || false,
        image: post.image,
        tags: post.tags || [],
        seo_title: post.seoTitle || post.seo_title,
        seo_description: post.seoDescription || post.seo_description,
        slug: post.slug,
        created_at: post.createdAt || post.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      console.log('📊 Prepared data for Supabase:', supabaseData);

      const { data, error } = await supabase
        .from('blog_posts_ai2024')
        .upsert(supabaseData, {
          onConflict: 'id',
          ignoreDuplicates: false
        })
        .select();

      if (error) {
        console.error('❌ Supabase upsert error:', error);
        throw error;
      }

      console.log('✅ Blog post saved to Supabase successfully:', data[0]);
      return { success: true, data: data[0] };
    } catch (error) {
      console.error('❌ Error saving blog post to Supabase:', error);
      console.log('🔄 Falling back to localStorage');
      return this.saveToLocalStorage('blog_posts', post);
    }
  },

  async getAllBlogPosts() {
    if (!isSupabaseConnected) {
      return this.getFromLocalStorage('blog_posts');
    }

    try {
      const { data, error } = await supabase
        .from('blog_posts_ai2024')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Convert Supabase column names back to frontend format
      const convertedData = data.map(post => ({
        ...post,
        readTime: post.read_time,
        seoTitle: post.seo_title,
        seoDescription: post.seo_description,
        createdAt: post.created_at,
        updatedAt: post.updated_at
      }));

      console.log('✅ Retrieved blog posts from Supabase:', convertedData.length);
      return convertedData || [];
    } catch (error) {
      console.error('❌ Error fetching blog posts from Supabase:', error);
      return this.getFromLocalStorage('blog_posts');
    }
  },

  async deleteBlogPost(id) {
    if (!isSupabaseConnected) {
      return this.deleteFromLocalStorage('blog_posts', id);
    }

    try {
      console.log('🗑️ Deleting blog post from Supabase:', id);
      const { error } = await supabase
        .from('blog_posts_ai2024')
        .delete()
        .eq('id', id);

      if (error) throw error;
      console.log('✅ Blog post deleted from Supabase');
      return { success: true };
    } catch (error) {
      console.error('❌ Error deleting blog post from Supabase:', error);
      return this.deleteFromLocalStorage('blog_posts', id);
    }
  },

  // Authors 
  async saveAuthor(author) {
    if (!isSupabaseConnected) {
      return this.saveToLocalStorage('authors', author);
    }

    try {
      // Prepare the data for Supabase
      const supabaseData = {
        id: author.id,
        name: author.name,
        slug: author.slug,
        title: author.title,
        bio: author.bio,
        avatar: author.avatar,
        email: author.email,
        website: author.website,
        location: author.location,
        years_experience: author.yearsExperience || author.years_experience || 0,
        specialties: author.specialties || [],
        social: author.social || {},
        achievements: author.achievements || [],
        languages: author.languages || [],
        status: author.status || 'active',
        featured: author.featured || false,
        join_date: author.joinDate || author.join_date,
        created_at: author.createdAt || author.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('authors_ai2024')
        .upsert(supabaseData, {
          onConflict: 'id',
          ignoreDuplicates: false
        })
        .select();

      if (error) throw error;
      return { success: true, data: data[0] };
    } catch (error) {
      console.error('Error saving author:', error);
      return this.saveToLocalStorage('authors', author);
    }
  },

  async getAllAuthors() {
    if (!isSupabaseConnected) {
      return this.getFromLocalStorage('authors');
    }

    try {
      const { data, error } = await supabase
        .from('authors_ai2024')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Convert Supabase column names back to frontend format
      const convertedData = data.map(author => ({
        ...author,
        yearsExperience: author.years_experience,
        joinDate: author.join_date,
        createdAt: author.created_at,
        updatedAt: author.updated_at
      }));

      return convertedData || [];
    } catch (error) {
      console.error('Error fetching authors:', error);
      return this.getFromLocalStorage('authors');
    }
  },

  async deleteAuthor(id) {
    if (!isSupabaseConnected) {
      return this.deleteFromLocalStorage('authors', id);
    }

    try {
      console.log('Deleting author from Supabase:', id);
      const { error } = await supabase
        .from('authors_ai2024')
        .delete()
        .eq('id', id);

      if (error) throw error;
      console.log('✅ Author deleted from Supabase');
      return { success: true };
    } catch (error) {
      console.error('Error deleting author from Supabase:', error);
      return this.deleteFromLocalStorage('authors', id);
    }
  },

  // Case Studies
  async saveCaseStudy(caseStudy) {
    if (!isSupabaseConnected) {
      return this.saveToLocalStorage('case_studies', caseStudy);
    }

    try {
      console.log('Saving case study to Supabase:', caseStudy);
      
      // Prepare the data for Supabase
      const supabaseData = {
        id: caseStudy.id,
        title: caseStudy.title,
        client: caseStudy.client,
        industry: caseStudy.industry,
        project_type: caseStudy.projectType || caseStudy.project_type,
        challenge: caseStudy.challenge,
        solution: caseStudy.solution,
        results: caseStudy.results || [],
        testimonial: caseStudy.testimonial,
        author: caseStudy.author,
        image: caseStudy.image,
        gallery: caseStudy.gallery || [],
        project_url: caseStudy.projectUrl || caseStudy.project_url,
        demo_url: caseStudy.demoUrl || caseStudy.demo_url,
        technologies: caseStudy.technologies || [],
        duration: caseStudy.duration,
        team_size: caseStudy.teamSize || caseStudy.team_size,
        featured: caseStudy.featured || false,
        status: caseStudy.status || 'active',
        created_at: caseStudy.createdAt || caseStudy.created_at || new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('case_studies_ai2024')
        .upsert(supabaseData, {
          onConflict: 'id',
          ignoreDuplicates: false
        })
        .select();

      if (error) throw error;
      console.log('✅ Case study saved to Supabase:', data[0]);
      return { success: true, data: data[0] };
    } catch (error) {
      console.error('Error saving case study to Supabase:', error);
      return this.saveToLocalStorage('case_studies', caseStudy);
    }
  },

  async getAllCaseStudies() {
    if (!isSupabaseConnected) {
      return this.getFromLocalStorage('case_studies');
    }

    try {
      console.log('Fetching case studies from Supabase...');
      const { data, error } = await supabase
        .from('case_studies_ai2024')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Convert Supabase column names back to frontend format
      const convertedData = data.map(study => ({
        ...study,
        projectType: study.project_type,
        projectUrl: study.project_url,
        demoUrl: study.demo_url,
        teamSize: study.team_size,
        createdAt: study.created_at,
        updatedAt: study.updated_at
      }));

      console.log('✅ Case studies fetched from Supabase:', convertedData);
      return convertedData || [];
    } catch (error) {
      console.error('Error fetching case studies from Supabase:', error);
      return this.getFromLocalStorage('case_studies');
    }
  },

  async deleteCaseStudy(id) {
    if (!isSupabaseConnected) {
      return this.deleteFromLocalStorage('case_studies', id);
    }

    try {
      console.log('Deleting case study from Supabase:', id);
      const { error } = await supabase
        .from('case_studies_ai2024')
        .delete()
        .eq('id', id);

      if (error) throw error;
      console.log('✅ Case study deleted from Supabase');
      return { success: true };
    } catch (error) {
      console.error('Error deleting case study from Supabase:', error);
      return this.deleteFromLocalStorage('case_studies', id);
    }
  },

  // Contact Messages
  async saveContactMessage(message) {
    if (!isSupabaseConnected) {
      return this.saveToLocalStorage('contact_messages', message);
    }

    try {
      const { data, error } = await supabase
        .from('contact_messages_ai2024')
        .insert({
          id: message.id,
          full_name: message.fullName || message.full_name,
          company_name: message.companyName || message.company_name,
          work_email: message.workEmail || message.work_email,
          phone_number: message.phoneNumber || message.phone_number,
          project_type: message.projectType || message.project_type,
          project_description: message.projectDescription || message.project_description,
          hear_about_us: message.hearAboutUs || message.hear_about_us,
          read: message.read || false,
          timestamp: message.timestamp || new Date().toISOString()
        })
        .select();

      if (error) throw error;
      return { success: true, data: data[0] };
    } catch (error) {
      console.error('Error saving contact message:', error);
      return this.saveToLocalStorage('contact_messages', message);
    }
  },

  async getAllContactMessages() {
    if (!isSupabaseConnected) {
      return this.getFromLocalStorage('contact_messages');
    }

    try {
      const { data, error } = await supabase
        .from('contact_messages_ai2024')
        .select('*')
        .order('timestamp', { ascending: false });

      if (error) throw error;
      
      // Convert Supabase column names back to frontend format
      const convertedData = data.map(message => ({
        ...message,
        fullName: message.full_name,
        companyName: message.company_name,
        workEmail: message.work_email,
        phoneNumber: message.phone_number,
        projectType: message.project_type,
        projectDescription: message.project_description,
        hearAboutUs: message.hear_about_us
      }));

      return convertedData || [];
    } catch (error) {
      console.error('Error fetching contact messages:', error);
      return this.getFromLocalStorage('contact_messages');
    }
  },

  // Media Files
  async saveMediaFile(file) {
    if (!isSupabaseConnected) {
      return this.saveToLocalStorage('media_files', file);
    }

    try {
      const { data, error } = await supabase
        .from('media_files_ai2024')
        .insert({
          id: file.id,
          name: file.name,
          url: file.url,
          path: file.path,
          type: file.type,
          size: file.size,
          used_in: file.used_in || file.usedIn || [],
          uploaded_at: file.uploaded_at || file.uploadedAt || new Date().toISOString()
        })
        .select();

      if (error) throw error;
      return { success: true, data: data[0] };
    } catch (error) {
      console.error('Error saving media file:', error);
      return this.saveToLocalStorage('media_files', file);
    }
  },

  async getAllMediaFiles() {
    if (!isSupabaseConnected) {
      return this.getFromLocalStorage('media_files');
    }

    try {
      const { data, error } = await supabase
        .from('media_files_ai2024')
        .select('*')
        .order('uploaded_at', { ascending: false });

      if (error) throw error;
      
      // Convert Supabase column names back to frontend format
      const convertedData = data.map(file => ({
        ...file,
        usedIn: file.used_in,
        uploadedAt: file.uploaded_at
      }));

      return convertedData || [];
    } catch (error) {
      console.error('Error fetching media files:', error);
      return this.getFromLocalStorage('media_files');
    }
  },

  async deleteMediaFile(id) {
    if (!isSupabaseConnected) {
      return this.deleteFromLocalStorage('media_files', id);
    }

    try {
      const { error } = await supabase
        .from('media_files_ai2024')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting media file:', error);
      return this.deleteFromLocalStorage('media_files', id);
    }
  },

  // File Upload to Supabase Storage
  async uploadFile(file, bucket = 'media') {
    if (!isSupabaseConnected) {
      // Fallback to optimized data URL for localStorage
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          // Use a placeholder for large files to avoid localStorage quota
          if (e.target.result.length > 500 * 1024) { // 500KB limit
            resolve({
              success: true,
              url: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&h=400&fit=crop',
              path: `placeholder/${Date.now()}_${file.name}`,
              _isPlaceholder: true
            });
          } else {
            resolve({
              success: true,
              url: e.target.result,
              path: `local/${Date.now()}_${file.name}`
            });
          }
        };
        reader.readAsDataURL(file);
      });
    }

    try {
      const fileName = `${Date.now()}_${file.name}`;
      const filePath = `${bucket}/${fileName}`;

      const { data, error } = await supabase.storage
        .from('aiximo-media')
        .upload(filePath, file);

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('aiximo-media')
        .getPublicUrl(filePath);

      return {
        success: true,
        url: publicUrl,
        path: filePath
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      // Fallback to optimized data URL
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve({
            success: true,
            url: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&h=400&fit=crop',
            path: `fallback/${Date.now()}_${file.name}`,
            _isFallback: true
          });
        };
        reader.readAsDataURL(file);
      });
    }
  },

  // LocalStorage fallback methods with quota management
  saveToLocalStorage(key, item) {
    try {
      // Check quota before saving
      const quota = checkStorageQuota();
      
      if (quota.isNearLimit) {
        console.warn('⚠️ localStorage is near capacity, cleaning up...');
        cleanupLocalStorage();
      }
      
      // Optimize data for localStorage
      const optimizedItem = optimizeForLocalStorage(item);
      
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      const itemIndex = existing.findIndex(i => i.id === optimizedItem.id);
      
      if (itemIndex >= 0) {
        existing[itemIndex] = optimizedItem;
        console.log(`📝 Updated ${key} item in localStorage:`, optimizedItem.id);
      } else {
        existing.unshift(optimizedItem);
        console.log(`➕ Added new ${key} item to localStorage:`, optimizedItem.id);
      }
      
      // Try to save, with fallback handling
      try {
        localStorage.setItem(key, JSON.stringify(existing));
        console.log(`✅ Saved to localStorage: ${key}`, optimizedItem);
        return { success: true, data: optimizedItem };
      } catch (quotaError) {
        if (quotaError.name === 'QuotaExceededError') {
          console.warn('💾 Quota exceeded, attempting emergency cleanup...');
          
          // Emergency cleanup: remove older items
          const trimmed = existing.slice(0, Math.max(1, Math.floor(existing.length * 0.7)));
          localStorage.setItem(key, JSON.stringify(trimmed));
          
          console.log(`🗑️ Trimmed ${key} from ${existing.length} to ${trimmed.length} items`);
          return {
            success: true,
            data: optimizedItem,
            warning: 'Storage was full, older items were removed to make space'
          };
        }
        throw quotaError;
      }
    } catch (error) {
      console.error(`❌ Error saving to localStorage (${key}):`, error);
      
      // Last resort: try to save just the essential data
      if (error.name === 'QuotaExceededError') {
        try {
          const essential = {
            id: item.id,
            title: item.title || item.name,
            status: item.status || 'draft',
            created_at: new Date().toISOString(),
            _minimal: true
          };
          
          const existing = JSON.parse(localStorage.getItem(key) || '[]').slice(0, 5); // Keep only 5 items
          const itemIndex = existing.findIndex(i => i.id === essential.id);
          
          if (itemIndex >= 0) {
            existing[itemIndex] = essential;
          } else {
            existing.unshift(essential);
          }
          
          localStorage.setItem(key, JSON.stringify(existing));
          
          return {
            success: true,
            data: essential,
            warning: 'Saved minimal data due to storage constraints. Please connect to Supabase for full functionality.'
          };
        } catch (finalError) {
          return {
            success: false,
            error: 'Storage quota exceeded and cleanup failed. Please clear browser data or connect to Supabase.'
          };
        }
      }
      
      return { success: false, error: error.message };
    }
  },

  getFromLocalStorage(key) {
    try {
      const data = JSON.parse(localStorage.getItem(key) || '[]');
      console.log(`📖 Retrieved from localStorage: ${key}`, data.length, 'items');
      return data;
    } catch (error) {
      console.error(`❌ Error reading from localStorage (${key}):`, error);
      return [];
    }
  },

  deleteFromLocalStorage(key, id) {
    try {
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      const filtered = existing.filter(item => item.id !== id);
      
      if (existing.length !== filtered.length) {
        localStorage.setItem(key, JSON.stringify(filtered));
        console.log(`✅ Deleted from localStorage: ${key} - ${id}`);
        return { success: true };
      } else {
        console.log(`❌ Item not found in localStorage: ${key} - ${id}`);
        return { success: false, error: 'Item not found' };
      }
    } catch (error) {
      console.error(`❌ Error deleting from localStorage (${key}):`, error);
      return { success: false, error: error.message };
    }
  }
};

// Connection status and utilities
export const isConnected = () => isSupabaseConnected;

export const getStorageInfo = () => {
  const quota = checkStorageQuota();
  return {
    isSupabaseConnected,
    localStorage: quota
  };
};

export const forceSupabaseConnection = async () => {
  console.log('🔄 Forcing Supabase connection attempt...');
  isSupabaseConnected = await testConnection();
  
  if (isSupabaseConnected) {
    console.log('✅ Supabase connection established');
    // Show success message
    return true;
  } else {
    console.log('❌ Supabase connection failed');
    return false;
  }
};